import React, { useCallback, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '&, & *': {
      userSelect: 'none',
    },
  },
  content: {
    maxHeight: '100%',
    display: 'flex',
    justifyContent: 'center',
    cursor: 'crosshair',
    flex: 1,
  },
}));

export const Magnifier = ({ minifiedChildren, magnifiedChildren }) => {
  const [state, setState] = useState({ x: 0, y: 0, scale: 1 });
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const activeRef = useRef(false);
  const handleMouseEvent = useCallback((evt) => {
    const { width, height, top, left } = activeRef.current;
    const { clientX, clientY } = evt.touches?.length ? evt.touches[0] : evt;

    const scale = Math.max(2048 / contentRef.current.clientHeight, 2);
    setState((state) => ({
      ...state,
      x: (-(Math.max(0, Math.min(width, clientX - left)) / width) + 0.5) * (scale - 1) * 100,
      y: -(Math.max(0, Math.min(height, clientY - top)) / height) * (scale - 1) * 100,
      scale,
    }));
  }, []);

  const handleStart = useCallback(
    (evt) => {
      activeRef.current = contentRef.current.getBoundingClientRect();
      handleMouseEvent(evt);
      const isTouch = evt.touches?.length > 0;

      const handleMove = (evt) => {
        // prevent pull-down reload and other unwanted effects
        evt.preventDefault();
        evt.stopPropagation();
        evt.cancelBubble = true;
        evt.returnValue = false;
        activeRef.current && handleMouseEvent(evt);
        return false;
      };

      const handleStop = () => {
        activeRef.current = false;
        setState({ x: 0, y: 0, scale: 1 });
        window.removeEventListener(isTouch ? 'touchmove' : 'mousemove', handleMove, { passive: false });
        window.removeEventListener(isTouch ? 'touchend' : 'mouseout', handleStop);
      };

      window.addEventListener(isTouch ? 'touchmove' : 'mousemove', handleMove, { passive: false });
      window.addEventListener(isTouch ? 'touchend' : 'mouseout', handleStop);
    },
    [handleMouseEvent]
  );

  const classes = useStyles();

  return (
    <div className={classes.root} ref={containerRef} onTouchStart={handleStart} onMouseOver={handleStart}>
      <div
        className={classes.content}
        ref={contentRef}
        style={{
          transform: `translate(${state.x}%, ${state.y}%)`,
        }}
      >
        {activeRef.current ? magnifiedChildren : minifiedChildren}
      </div>
    </div>
  );
};

export default Magnifier;
